const _ = require('lodash');
const { ReplaySubject } = require('rxjs');

const Ajv = require("ajv");
const addFormats = require("ajv-formats");

const SCHEMA_CATEGORIES = {
  anyOf: [
    { type: 'array', items: { type: 'string', minLength: 1 } },
    { type: 'string' },
  ]
};

const SCHEMA_CONFIG = {
  type: 'object',
  required: [ 'host', 'orgId', 'appId', 'categories' ],
  additionalProperties: false,
  properties: {
    host: { type: 'string', format: 'uri' },
    orgId: { type: 'string', format: 'uuid' },
    orgName: { type: 'string' },
    appId: { type: 'string', minLength: 1 },
    categories: SCHEMA_CATEGORIES,
  }
};

function createAjv() {
  const ajv = new Ajv();
  addFormats(ajv);
  return ajv;
}

/**
 * @namespace ConfigService
 *
 * @description By default, the configuration service will try to look in the environment for a Hive configuration. If a full configuration
 * is found, the configuration service will automatically hook to it. The service looks for the following variables:
 *  - host: HIVE_HOST or REACT_APP_HIVE_HOST (in that order)
 *  - orgId: HIVE_ORG_ID or REACT_APP_HIVE_ORG_ID (in that order)
 *  - appId: HIVE_APP_ID or REACT_APP_HIVE_APP_ID (in that order)
 *  - categories: HIVE_CATEGORIES or REACT_APP_HIVE_CATEGORIES (in that order)
 *  - orgName: HIVE_ORG_NAME or REACT_APP_HIVE_ORG_NAME (in that order)
 *  - rolesToCheck: HIVE_ROLES_TO_CHECK or REACT_APP_HIVE_ROLES_TO_CHECK (in that order)
 */

/**
 * @constant config$
 * @memberof ConfigService
 * @description An observable that can be subscribed to in order to get updates about the config to Hive. Refer
 * to [RxJS Observable](http://reactivex.io/rxjs/class/es6/Observable.js~Observable.html) for more details.
 */
const _config$ = new ReplaySubject(1);

/**
 * @function changeConfig
 * @memberof ConfigService
 * @description Changes the configuration object used.
 *
 * @param {URL} host - the URL to the Hive instance to connection
 * @param {UUID} orgId - the ID of the organization to connect to on the Hive instance
 * @param {String} appId - the ID of the application to connect to
 * @param {String[]} categories - an array of categories to register to on the Hive instance. Defaults to
 * [ 'core', 'storage' ]. Can be passed as a stringified array.
 * @param {String?} orgName - (Optional) the name of the organization.
 * @param {String[]?} rolesToCheck - (Optional) a list of roles to check on the token to continue with the login process.
 *
 * @returns {Object} the generated config object.
 */
function changeConfig(host, orgId, appId, categories = [ 'core', 'storage' ], orgName = "", rolesToCheck = []) {

  const ajv = createAjv();
  if (!ajv.validate(SCHEMA_CONFIG, { host, orgId, appId, categories, orgName })) {
    throw new Error(ajv.errorsText());
  }

  if (_.isString(categories)) {
    categories = JSON.parse(categories);
    if (!ajv.validate(SCHEMA_CATEGORIES, categories)) {
      throw new Error(ajv.errorsText());
    }
  }

  const config = {
    host,
    orgId,
    orgName,
    appId,
    categories,
    validation: {
      rolesToCheck,
    },
  };

  console.log('Setting config:', config);
  _config$.next(config);

  return config;
}

/**
 * @function clearConfig
 * @memberof ConfigService
 * @Description Removes any current configuration
 */
function clearConfig() {
  console.log('Clearing config');
  _config$.next(undefined);
}

/**
 * @function getConfig
 * @memberof ConfigService
 * @description Returns the current configuration, if any.
 * @returns {Object} the current configuration, can be null.
 */
function getConfig() {
  let config;
  _config$
    .subscribe(c => (config = c))
    .unsubscribe();

  return config;
}

/**
 * @function assertConfig
 * @memberof ConfigService
 * @description Asserts that there is a current configuration. If there is non, an exception is raised.
 *
 * @returns {Object} the current configuration. If it returns, the configuration can never be null.
 * @throws an an error if there is no configuration.
 */
function assertConfig() {
  const config = getConfig();
  if (!config) {
    throw new Error("Config is needed!");
  }
  return config;
}

function findFirstEnvVariableByNames(...variableNames) {
  for (let variableName of variableNames) {
    const value = _.get(process.env, variableName)
      || _.get(window._env_, variableName)
    ;
    if (value) {
      return value;
    }
  }
};

function findAndFirstEnvVariableByNames(...variableNames) {
  let value = findFirstEnvVariableByNames(...variableNames);
  try {
    if (_.isString(value)) {
      value = JSON.parse(value);
    }
  } catch (ignored) {
  }

  return value;
}

// Try to load the base configuration from environment
// We do support for React variable names since we don't need a library to do so.
const HOST = findFirstEnvVariableByNames("HIVE_HOST", "REACT_APP_HIVE_HOST");
const ORG_ID = findFirstEnvVariableByNames("HIVE_ORG_ID", "REACT_APP_HIVE_ORG_ID");
const ORG_NAME = findFirstEnvVariableByNames("HIVE_ORG_NAME", "REACT_APP_HIVE_ORG_NAME");
const APP_ID = findFirstEnvVariableByNames("HIVE_APP_ID", "REACT_APP_HIVE_APP_ID");
const CATEGORIES = findAndFirstEnvVariableByNames("HIVE_CATEGORIES", "REACT_APP_HIVE_CATEGORIES");
const ROLES_TO_CHECK = findAndFirstEnvVariableByNames("HIVE_ROLES_TO_CHECK", "REACT_APP_HIVE_ROLES_TO_CHECK");
if (HOST && ORG_ID && APP_ID) {
  console.info('Found configuration in environment variables');
  changeConfig(HOST, ORG_ID, APP_ID, CATEGORIES, ORG_NAME, ROLES_TO_CHECK);
} else {
  console.warn('No configuration in environment variables');
}

module.exports = {
  config$: _config$,
  changeConfig,
  clearConfig,
  getConfig,
  assertConfig,
};
